import React from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle2, ArrowRight, ExternalLink, BookOpen } from 'lucide-react';

const Onboarding = () => {
  const steps = [
    {
      title: 'Étape 1: Créer un projet Google Cloud',
      description: 'Créez un nouveau projet dans Google Cloud Console',
      actions: [
        'Accédez à console.cloud.google.com',
        'Créez un nouveau projet ou sélectionnez-en un existant',
        'Notez le Project ID pour plus tard'
      ],
      link: 'https://console.cloud.google.com'
    },
    {
      title: 'Étape 2: Activer l\'API RCS Business Messaging',
      description: 'Activez l\'API RCS dans votre projet Google Cloud',
      actions: [
        'Dans Google Cloud Console, allez dans APIs & Services',
        'Recherchez "RCS Business Messaging API"',
        'Cliquez sur "Enable" pour activer l\'API'
      ],
      link: 'https://console.cloud.google.com/apis/library'
    },
    {
      title: 'Étape 3: Créer un Service Account',
      description: 'Créez un compte de service pour l\'authentification',
      actions: [
        'Allez dans IAM & Admin > Service Accounts',
        'Créez un nouveau service account',
        'Accordez le rôle "RCS Business Messaging API Admin"',
        'Générez et téléchargez une clé JSON'
      ],
      link: 'https://console.cloud.google.com/iam-admin/serviceaccounts'
    },
    {
      title: 'Étape 4: S\'inscrire à RCS Business Messaging',
      description: 'Créez votre agent RCS et votre marque',
      actions: [
        'Accédez au portail RCS Business Messaging',
        'Créez une nouvelle marque (Brand)',
        'Créez un nouvel agent RCS',
        'Notez votre Brand ID et Agent ID'
      ],
      link: 'https://business-communications.cloud.google.com'
    },
    {
      title: 'Étape 5: Configurer les webhooks',
      description: 'Configurez les webhooks pour recevoir les confirmations de livraison',
      actions: [
        'Dans le portail RCS, allez dans les paramètres de votre agent',
        'Ajoutez votre URL de webhook: https://votre-domaine.com/api/webhooks/delivery',
        'Vérifiez que les webhooks sont activés pour les événements de livraison et lecture'
      ]
    },
    {
      title: 'Étape 6: Configuration dans B2RCS',
      description: 'Entrez vos credentials dans B2RCS',
      actions: [
        'Allez dans Paramètres > Configuration RCS',
        'Entrez votre Project ID, Service Account Email, Agent ID et Brand ID',
        'Collez le contenu de votre fichier JSON de clé privée',
        'Enregistrez la configuration'
      ]
    }
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8" data-testid="onboarding-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Guide d'onboarding Google RCS
          </h1>
          <p className="mt-2 text-slate-600">Suivez ces étapes pour configurer votre intégration Google RCS Business Messaging</p>
        </div>

        <Alert className="border-indigo-100 bg-indigo-50">
          <BookOpen className="h-5 w-5 text-indigo-700" />
          <AlertDescription className="text-indigo-900">
            Ce processus prend environ 30 minutes. Assurez-vous d'avoir accès à Google Cloud Console et aux droits nécessaires.
          </AlertDescription>
        </Alert>

        <div className="space-y-6">
          {steps.map((step, index) => (
            <Card key={index} className="rounded-2xl border-slate-100" data-testid={`step-${index + 1}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-700 text-white font-bold">
                        {index + 1}
                      </div>
                      <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{step.title}</CardTitle>
                    </div>
                    <CardDescription className="mt-2 ml-13">{step.description}</CardDescription>
                  </div>
                  {step.link && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => window.open(step.link, '_blank')}
                      data-testid={`external-link-${index + 1}`}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Ouvrir
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 ml-13">
                  {step.actions.map((action, actionIndex) => (
                    <li key={actionIndex} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{action}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="rounded-2xl border-indigo-200 bg-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="rounded-full bg-indigo-100 p-3">
                <BookOpen className="h-6 w-6 text-indigo-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-indigo-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Besoin d'aide ?
                </h3>
                <p className="mt-1 text-sm text-indigo-700">
                  Consultez la documentation officielle de Google RCS Business Messaging pour plus de détails ou contactez notre équipe support.
                </p>
                <Button
                  className="mt-4 rounded-full bg-indigo-700"
                  onClick={() => window.open('https://developers.google.com/business-communications/rcs-business-messaging', '_blank')}
                  data-testid="documentation-link"
                >
                  Documentation officielle
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Onboarding;